import { Controller } from "@hotwired/stimulus"

export default class CarouselController extends Controller {
  static targets = ["slide", "dot", "portal"]
  static values = {
    current: { type: Number, default: 0 },
    interval: { type: Number, default: 7000 },
  }

  declare readonly slideTargets: HTMLElement[]
  declare readonly dotTargets: HTMLElement[]
  declare readonly portalTarget: HTMLElement
  declare currentValue: number
  declare readonly intervalValue: number

  private timer: ReturnType<typeof setInterval> | null = null
  private handleMouseEnter = () => this.stopTimer()
  private handleMouseLeave = () => this.startTimer()

  connect() {
    this.showSlide(0)
    if (this.slideTargets.length > 1) {
      this.startTimer()
      this.element.addEventListener("mouseenter", this.handleMouseEnter)
      this.element.addEventListener("mouseleave", this.handleMouseLeave)
    }
  }

  disconnect() {
    this.stopTimer()
    this.element.removeEventListener("mouseenter", this.handleMouseEnter)
    this.element.removeEventListener("mouseleave", this.handleMouseLeave)
  }

  next() {
    this.transitionTo((this.currentValue + 1) % this.slideTargets.length)
  }

  prev() {
    this.transitionTo((this.currentValue - 1 + this.slideTargets.length) % this.slideTargets.length)
  }

  goTo({ params }: { params: { index: number } }) {
    this.transitionTo(params.index)
  }

  private transitionTo(index: number) {
    if (index === this.currentValue) return
    this.stopTimer()
    this.pulsePortal()
    this.showSlide(index)
    this.startTimer()
  }

  private showSlide(index: number) {
    this.currentValue = index
    this.slideTargets.forEach((slide, i) => {
      slide.dataset.active = String(i === index)
    })
    this.dotTargets.forEach((dot, i) => {
      dot.classList.toggle("carousel-dot--active", i === index)
    })
  }

  private pulsePortal() {
    const el = this.portalTarget
    el.classList.remove("carousel-portal--pulse")
    void el.offsetWidth
    el.classList.add("carousel-portal--pulse")
  }

  private startTimer() {
    this.stopTimer()
    if (this.slideTargets.length > 1) {
      this.timer = setInterval(() => this.next(), this.intervalValue)
    }
  }

  private stopTimer() {
    if (this.timer !== null) {
      clearInterval(this.timer)
      this.timer = null
    }
  }
}
